import { convertNumberArrayToLatLng } from '../map/map-helper';
import {
  SelectedSpreadingType,
  getSpreadingTypeEnumForString,
  getSpreadingTypeStringForEnum,
} from '../spreading-type-selector/spreading-type';
import {
  AdvancedMarker,
  AdvancedPolyline,
  FieldLineData,
} from '../map/map-drawing-logic';
import { SelectionModeNoneHandler } from '../selection-modes/selection-mode';
import { SelectionModeSegmentHandler } from '../selection-modes/selection-mode-segment';
import { easyTramMainlandCoverageColor } from '../map/map-drawing-colors';
import shpwrite from "@mapbox/shp-write"

import {
  Angle,
  Planner,
  TramlinePolygon,
  FullPlan,
  wgs84Point,
  Wgs84Point,
  wgs84Polygon3D,
  wgs84LineString,
  Wgs84LineString,
} from 'tramlib';
import { MapComponent } from '../map/map.component';
import { ZipArchive } from '@shortercode/webzip';
import { AES, ECB, Hex, Pkcs7 } from 'crypto-es';

export class Field {
  id: string | undefined;
  public name!: string;
  area!: number;
  type: string | undefined;
  polygon!: Polygon;

  isSelected: boolean | undefined;

  customerId: string | undefined;

  scenarios: Scenario[] | undefined;
  easyTramPlans: EasyTramPlan[] | undefined;

  createdBy: string | undefined;
  createdAt: Date | undefined;
  modifiedBy: string | undefined;
  modifiedAt: Date | undefined;
  deletedBy: string | undefined;
  deletedAt: Date | undefined;
  newField: any;

  equals(comp: Field | undefined): boolean {
    if (!comp) return false;

    if (this.id != comp.id) return false;
    if (this.name != comp.name) return false;
    if (this.type != comp.type) return false;

    //TODO: check if we need to compare dates as well

    if (!this.polygon.equals(comp.polygon)) return false;

    if (this.scenarios?.length != comp.scenarios?.length) return false;
    if (this.scenarios && this.scenarios.length > 0 && comp.scenarios) {
      for (let i = 0; i < this.scenarios.length; i++) {
        try {
          if (this.scenarios[i] && !this.scenarios[i].equals(comp.scenarios[i]))
            return false;
        } catch (e) {
          console.warn('Issue in equals for ', this.scenarios, comp.scenarios);
          console.warn(e);
          return false;
        }
      }
    }

    if (this.easyTramPlans?.length != comp.easyTramPlans?.length) return false;
    if (this.easyTramPlans && this.easyTramPlans.length > 0 && comp.easyTramPlans) {
      for (let i = 0; i < this.easyTramPlans.length; i++) {
        try {
          if (this.easyTramPlans[i] && !this.easyTramPlans[i].equals(comp.easyTramPlans[i]))
            return false;
        } catch (e) {
          console.warn('Issue in equals for ', this.easyTramPlans, comp.easyTramPlans);
          console.warn(e);
          return false;
        }
      }
    }

    return true;
  }

  getPaths4Polygon() {
    const paths: google.maps.LatLng[][] = [];
    for (const polygon of this.polygon.coordinates) {
      paths.push(convertNumberArrayToLatLng(polygon));
    }
    return paths;
  }
}

export function cloneField(oldField: Field): Field {
  const newField = new Field();
  newField.id = oldField.id;
  newField.name = oldField.name;
  newField.area = oldField.area;
  newField.type = oldField.type;
  newField.polygon = oldField.polygon;
  newField.isSelected = oldField.isSelected;
  newField.customerId = oldField.customerId;
  newField.scenarios = oldField.scenarios;
  newField.easyTramPlans = oldField.easyTramPlans;
  newField.createdAt = oldField.createdAt;
  newField.createdBy = oldField.createdBy;
  newField.modifiedAt = oldField.modifiedAt;
  newField.modifiedBy = oldField.modifiedBy;
  newField.deletedAt = oldField.deletedAt;
  newField.deletedBy = oldField.deletedBy;
  return newField;
}

export function renameField(oldField: Field, newName: string): Field {
  const newField = new Field();
  newField.id = oldField.id;
  newField.name = newName ? newName : oldField.name;
  newField.area = oldField.area;
  newField.type = oldField.type;
  newField.polygon = oldField.polygon;
  return newField;
}

export class Polygon {
  type!: string;

  coordinates!: number[][][];

  constructor(coord: number[][][], pType = 'Polygon') {
    this.type = pType;
    this.coordinates = coord;
  }

  equals(comp: Polygon): boolean {
    if (this.type != comp.type) return false;
    if (this.coordinates.length != comp.coordinates.length) return false;
    for (let i = 0; i < this.coordinates.length; i++) {
      if (this.coordinates[i].length != comp.coordinates[i].length)
        return false;
      for (let j = 0; j < this.coordinates[i].length; j++) {
        if (this.coordinates[i][j][0] != comp.coordinates[i][j][0])
          return false;
        if (this.coordinates[i][j][1] != comp.coordinates[i][j][1])
          return false;
      }
    }
    return true;
  }
}

export class Scenario {
  id!: string;
  name: string | undefined;
  state!: string;
  border!: ScenarioBorder;

  createdBy: string | undefined;
  createdAt: Date | undefined;
  modifiedBy: string | undefined;
  modifiedAt: Date | undefined;
  deletedBy: string | undefined;
  deletedAt: Date | undefined;

  equals(compScenario: Scenario | undefined): boolean {
    if (!compScenario) return false;

    if (this.id != compScenario.id) return false;
    if (this.name != compScenario.name) return false;
    if (this.state != compScenario.state) return false;

    //TODO: check if we need to compare dates as well

    if (!this.border.equals(compScenario.border)) return false;

    return true;
  }
}

export function renameScenario(
  oldScenario: Scenario,
  newName: string
): Scenario {
  const newScenario = new Scenario();
  newScenario.id = oldScenario.id;
  newScenario.name = newName ? newName : oldScenario.name;
  newScenario.state = oldScenario.state;
  newScenario.border = oldScenario.border;
  return newScenario;
}

export function cloneScenario(oldScenario: Scenario): Scenario {
  const newScenario = new Scenario();
  newScenario.id = oldScenario.id;
  newScenario.name = oldScenario.name;
  newScenario.state = oldScenario.state;
  newScenario.border = oldScenario.border;

  return newScenario;
}
export class ScenarioBorder {
  outer!: ScenarioBorderRing;
  inners: ScenarioBorderRing[] | undefined;

  equals(comp: ScenarioBorder): boolean {
    if (!this.outer.equals(comp.outer)) return false;
    if (!this.inners && comp.inners) return false;
    if (this.inners && !comp.inners) return false;
    if (this.inners) {
      if (this.inners?.length != comp.inners?.length) return false;
      for (let i = 0; i < this.inners.length; i++) {
        if (!this.inners[i].equals(comp.inners[i])) return false;
      }
    }
    return true;
  }

  clone(): ScenarioBorder {
    const newBorder = new ScenarioBorder();
    newBorder.outer = this.outer.clone();
    if (this.inners) {
      newBorder.inners = this.inners.map(inner => inner.clone());
    }
    return newBorder;
  }
}

export class ScenarioBorderRing {
  segments!: ScenarioBorderSegment[];

  constructor() {
    this.segments = [];
  }

  equals(comp: ScenarioBorderRing): boolean {
    if (this.segments.length != comp.segments.length) return false;

    for (let i = 0; i < this.segments.length; i++) {
      if (!this.segments[i].equals(comp.segments[i])) return false;
    }
    return true;
  }

  clone(): ScenarioBorderRing {
    const newRing = new ScenarioBorderRing();
    newRing.segments = this.segments.map(seg => seg.clone());
    return newRing;
  }

  getCoordinates(): number[][] {
    return this.segments.reduce<number[][]>((coordinates, segment) => {
      const segmentCoordinates = segment.coordinates.map(point => [
        point[0],
        point[1],
      ]);

      if (
        coordinates.length > 0 &&
        segmentCoordinates.length > 0 &&
        coordinates[coordinates.length - 1][0] === segmentCoordinates[0][0] &&
        coordinates[coordinates.length - 1][1] === segmentCoordinates[0][1]
      ) {
        segmentCoordinates.shift();
      }

      coordinates.push(...segmentCoordinates);
      return coordinates;
    }, []);
  }
}

export type EasyTramSegmentType = 'headlandGuidanceTrack' | 'headlandTramline' | 'mainlandGuidanceTrack' | 'mainlandTramline' | 'mainlandBoundary' | 'mainlandBaseSegment' | 'mainlandCoverage' | 'default';

export class ScenarioBorderSegment {
  coordinates!: number[][]; // array containing points of type [[x, y], [x, y], ...]
  outfacing!: string; //currently sent this way from Backend
  infacing!: string; //currently sent this way from Backend
  easyTramSegmentType: EasyTramSegmentType = 'default';

  getSpreadingType(): SelectedSpreadingType {
    return new SelectedSpreadingType(
      getSpreadingTypeEnumForString(this.outfacing),
      getSpreadingTypeEnumForString(this.infacing)
    );
  }

  setSpreadingType(spreadingType: SelectedSpreadingType) {
    this.outfacing = getSpreadingTypeStringForEnum(spreadingType.outfacing);
    this.infacing = getSpreadingTypeStringForEnum(spreadingType.infacing);
  }

  equals(comp: ScenarioBorderSegment): boolean {
    if (this.outfacing != comp.outfacing) return false;
    if (this.infacing != comp.infacing) return false;
    if (this.easyTramSegmentType != comp.easyTramSegmentType) return false;

    //check coordinates
    if (this.coordinates.length != comp.coordinates.length) return false;
    for (let i = 0; i < this.coordinates.length; i++) {
      if (this.coordinates[i].length != comp.coordinates[i].length)
        return false;
      for (let j = 0; j < this.coordinates[i].length; j++) {
        if (this.coordinates[i][j] != comp.coordinates[i][j]) return false;
      }
    }

    return true;
  }

  clone(): ScenarioBorderSegment {
    const newSeg = new ScenarioBorderSegment();
    newSeg.coordinates = this.coordinates.map(arr => [...arr]);
    newSeg.outfacing = this.outfacing;
    newSeg.infacing = this.infacing;
    newSeg.easyTramSegmentType = this.easyTramSegmentType;
    return newSeg;
  }
}

// type for the lines of the scenario border/shape
export type ScenarioLine = {
  id: string;
  line?: AdvancedPolyline; // single polyline-element of the border
  dotEnd?: AdvancedMarker;
  lineData?: FieldLineData;
};

export class EasyTramPlan {
  _planner!: Planner;

  id!: string;
  name: string | undefined;
  border!: ScenarioBorder;
  state!: EASYTRAMPLANSTATE;

  angleMax: number = 15.0;
  speedMax: number = 13.0 / 3.6;
  overlap: number = 100.0;

  showFieldCoverage: boolean = false;

  createdBy: string | undefined;
  createdAt: Date | undefined;
  modifiedBy: string | undefined;
  modifiedAt: Date | undefined;
  deletedBy: string | undefined;
  deletedAt: Date | undefined;

  /*
  plan: { // wird vom planner zurückgegeben
    _workingWidthCareDevice: number = 1;
    _trackWidthCareDevice: number = 1;
    _wheelWidthCareDevice: number = 1;

    // Headland parameters
    _headlandType: boolean = false; // true = with headland, false = without headland
    _headlandWidth: number = 0;
    _withTramlines: boolean = false;

    // Coreland parameters
    _coreLandReferenceLine: ScenarioBorderSegment | undefined;
    _shiftOffset: number = 0;
    _degreeOffset: number = 0;
  }
  */

  constructor(border: ScenarioBorder) {
    this.border = border;

    const polygonPoints: Wgs84Point[][] = [];
    polygonPoints.push(this.border.outer.segments.reduce<Wgs84Point[]>((points, segment) => {
      for (const coord of segment.coordinates) {
        points.push(wgs84Point(coord[0], coord[1]));
      }
      return points;
    }, []));
    for (const inner of this.border.inners || []) {
      if (inner.segments.length < 4) continue;

      const points = inner.segments.reduce<Wgs84Point[]>((points, segment) => {
        for (const coord of segment.coordinates) {
          points.push(wgs84Point(coord[0], coord[1]));
        }
        return points;
      }, []);
      // close the polygon for the planner by repeating the first point at the end
      points.push(points[0]); 
      polygonPoints.push(points);
    }

    this._planner = new Planner(wgs84Polygon3D(polygonPoints));
  }

  get workingWidthCareDevice(): number {
    return this._planner.plant_care_swath || 0;
  }

  set workingWidthCareDevice(value: number) {
    this._planner.plant_care_swath = value;
  }

  get trackWidthCareDevice(): number {
    return this._planner.wheel_track || 0;
  }

  set trackWidthCareDevice(value: number) {
    this._planner.wheel_track = value;
  }

  get wheelWidthCareDevice(): number {
    return this._planner.tire_width || 0;
  }

  set wheelWidthCareDevice(value: number) {
    this._planner.tire_width = value;
  }

  get headlandType(): 'complete' | 'off' {
    return this._planner.headland_mode || 'off';
  }

  set headlandType(value: 'complete' | 'off') {
    this._planner.headland_mode = value;
  }

  get headlandWidth(): number {
    return this._planner.headland_width || 0;
  }

  set headlandWidth(value: number) {
    this._planner.headland_width = value;
  }

  get withTramlines(): boolean {
    return this._planner.headland_tramlines || false;
  }

  set withTramlines(value: boolean) {
    this._planner.headland_tramlines = value;
  }

  get shiftOffset(): number {
    return this._planner.coverage_line_offset || 0;
  }

  set shiftOffset(value: number) {
    this._planner.coverage_line_offset = value;
  }

  get degreeOffset(): number {
    return this._planner.coverage_line_offset_angle?.as_degree() || 0;
  }

  set degreeOffset(value: number) {
    this._planner.coverage_line_offset_angle = Angle.from_degree(value);
  }

  get corelandBaseSegment(): ScenarioBorderSegment | undefined {
    if (this._planner.coverage_reference_line === undefined) {
      return undefined;
    }
    const segment = new ScenarioBorderSegment();
    segment.coordinates = this._planner.coverage_reference_line
      .coordinates
      .map(coord => [coord[0], coord[1]]);
    segment.outfacing = 'normal';
    segment.infacing = 'normal'; 
    segment.easyTramSegmentType = 'mainlandBaseSegment';
    return segment;
  }

  set corelandBaseSegment(value: ScenarioBorderSegment | undefined) {
    if (value !== undefined) {
      this._planner.coverage_reference_line = wgs84LineString(value.coordinates.map(coord => wgs84Point(coord[0], coord[1])));
    }
  }

  get planData(): FullPlan | undefined {
    try {
      return this._planner.full_plan;
    } catch (e) {
      // Maybe just throw the error to indicate that we are not ready to export/save this plan yet?
      return undefined;
    }
  }

  clone(): EasyTramPlan {
    // Deep clone the border to avoid shared references
    const newBorder = this.border.clone();
    const newPlan = new EasyTramPlan(newBorder);

    newPlan.id = this.id;
    newPlan.name = this.name;
    newPlan.state = this.state;

    // Set all planner-relevant properties via setters to ensure the new Planner is configured identically
    newPlan.workingWidthCareDevice = this.workingWidthCareDevice;
    newPlan.trackWidthCareDevice = this.trackWidthCareDevice;
    newPlan.wheelWidthCareDevice = this.wheelWidthCareDevice;
    newPlan.angleMax = this.angleMax;
    newPlan.speedMax = this.speedMax;
    newPlan.overlap = this.overlap;

    newPlan.headlandType = this.headlandType;
    newPlan.headlandWidth = this.headlandWidth;
    newPlan.withTramlines = this.withTramlines;

    newPlan.shiftOffset = this.shiftOffset;
    newPlan.degreeOffset = this.degreeOffset;
    newPlan.corelandBaseSegment = this.corelandBaseSegment;

    newPlan.showFieldCoverage = this.showFieldCoverage;

    return newPlan;
  }

  equals(compScenario: EasyTramPlan | undefined): boolean {
    if (!compScenario) return false;

    if (this.id != compScenario.id) return false;
    if (this.name != compScenario.name) return false;
    if (this.state != compScenario.state) return false;

    //TODO: check if we need to compare dates as well

    if (!this.border.equals(compScenario.border)) return false;

    if (this.workingWidthCareDevice != compScenario.workingWidthCareDevice) return false;
    if (this.trackWidthCareDevice != compScenario.trackWidthCareDevice) return false;
    if (this.wheelWidthCareDevice != compScenario.wheelWidthCareDevice) return false;
    if (this.angleMax != compScenario.angleMax) return false;
    if (this.speedMax != compScenario.speedMax) return false;
    if (this.overlap != compScenario.overlap) return false;

    if (this.headlandType != compScenario.headlandType) return false;
    if (this.headlandWidth != compScenario.headlandWidth) return false;
    if (this.withTramlines != compScenario.withTramlines) return false;

    if (this.shiftOffset != compScenario.shiftOffset) return false;
    if (this.degreeOffset != compScenario.degreeOffset) return false;
    if (this.corelandBaseSegment != compScenario.corelandBaseSegment) return false;

    if (this.showFieldCoverage != compScenario.showFieldCoverage) return false;

    return true;
  }

  mainlandBoundary(): ScenarioBorderRing | undefined {
    const mainlandBoundary = this._planner.mainland_boundary;
    if (mainlandBoundary !== undefined) {
      return mainlandBoundary.coordinates.reduce((borderRing, mainlandPolygonPoints) => {
        for (let i = 1; i < mainlandPolygonPoints.length; i++) {
          const nextSegment = new ScenarioBorderSegment();
          nextSegment.easyTramSegmentType = 'mainlandBoundary';
          nextSegment.coordinates = [
            [mainlandPolygonPoints[i - 1][0], mainlandPolygonPoints[i - 1][1]],
            [mainlandPolygonPoints[i][0], mainlandPolygonPoints[i][1]],
          ];
          borderRing.segments.push(nextSegment);
        }
        return borderRing;
      }, new ScenarioBorderRing());
    }

    return undefined;
  }

  headlandGuidanceTracks(): ScenarioBorderRing[] | undefined {
    if (!this.validateMachineConfiguration() || !this.validateHeadlandConfiguration() || !this.headlandType) {
      // No valid configuration for planner to calculate guidance tracks
      return undefined;
    }

    // Compute the resulting headland guidance track
    const guidanceTracks = this._planner.headland_guidance_tracks;
    if (guidanceTracks !== undefined) {
      // Calculated guidance track so we can add it to the easy tram plan for visualization in the map
      return guidanceTracks.map(guidanceTrack=> {
        const borderRing = new ScenarioBorderRing();
        for (let i = 1; i < guidanceTrack.coordinates.length; i++) {
          const nextSegment = new ScenarioBorderSegment();
          nextSegment.easyTramSegmentType = 'headlandGuidanceTrack';
          nextSegment.coordinates = [
            [guidanceTrack.coordinates[i - 1][0], guidanceTrack.coordinates[i - 1][1]],
            [guidanceTrack.coordinates[i][0], guidanceTrack.coordinates[i][1]],
          ];
          borderRing.segments.push(nextSegment);
        }
        return borderRing;
      });
    }

    return undefined;
  }

  // Returns [[left tramline, right tramline], [l, r], ...]
  headlandTramlinePolygons(): ScenarioBorderRing[][] | undefined {
    if (!this.validateMachineConfiguration() || !this.validateHeadlandConfiguration() || !this.headlandType) {
      // No valid configuration for planner to calculate guidance tracks
      return undefined;
    }

    const tramlinePolygons = this._planner.headland_tramline_polygons;
    console.log('headlandTramlinePolygons: ', tramlinePolygons);
    if (tramlinePolygons !== undefined) {
      return tramlinePolygons.map(headlandTramlinePolygon => {
        const left = new ScenarioBorderRing();
        for (const points of headlandTramlinePolygon.left.coordinates) {
          for (let i = 1; i < points.length; i++) {
            const nextSegment = new ScenarioBorderSegment();
            nextSegment.easyTramSegmentType = 'headlandTramline';
            nextSegment.coordinates = [
              [points[i - 1][0], points[i - 1][1]],
              [points[i][0], points[i][1]],
            ];
            left.segments.push(nextSegment);
          }
        }
        const right = new ScenarioBorderRing();
        for (const points of headlandTramlinePolygon.right.coordinates) {
          for (let i = 1; i < points.length; i++) {
            const nextSegment = new ScenarioBorderSegment();
            nextSegment.easyTramSegmentType = 'headlandTramline';
            nextSegment.coordinates = [
              [points[i - 1][0], points[i - 1][1]],
              [points[i][0], points[i][1]],
            ];
            right.segments.push(nextSegment);
          }
        }

        return [left, right];
      });
    }

    return undefined;
  }

  mainlandGuidanceTracks(): ScenarioBorderRing[] | undefined {
    const guidanceTracks = this._planner.mainland_guidance_tracks;
    if (guidanceTracks !== undefined) {
      // Calculated guidance track so we can add it to the easy tram plan for visualization in the map
      return guidanceTracks.map(guidanceTrack=> {
        const borderRing = new ScenarioBorderRing();
        for (let i = 1; i < guidanceTrack.coordinates.length; i++) {
          const nextSegment = new ScenarioBorderSegment();
          nextSegment.easyTramSegmentType = 'mainlandGuidanceTrack';
          nextSegment.coordinates = [
            [guidanceTrack.coordinates[i - 1][0], guidanceTrack.coordinates[i - 1][1]],
            [guidanceTrack.coordinates[i][0], guidanceTrack.coordinates[i][1]],
          ];
          borderRing.segments.push(nextSegment);
        }
        return borderRing;
      });
    }

    return undefined;
  }

  // Returns [[left tramline, right tramline], [l, r], ...]
  mainlandTramlinePolygons(): ScenarioBorderRing[][] | undefined {
    const tramlinePolygons = this._planner.mainland_tramline_polygons;
    if (tramlinePolygons !== undefined) {
      return tramlinePolygons.map(mainlandTramlinePolygon => {
        const left = new ScenarioBorderRing();
        for (const points of mainlandTramlinePolygon.left.coordinates) {
          for (let i = 1; i < points.length; i++) {
            const nextSegment = new ScenarioBorderSegment();
            nextSegment.easyTramSegmentType = 'mainlandTramline';
            nextSegment.coordinates = [
              [points[i - 1][0], points[i - 1][1]],
              [points[i][0], points[i][1]],
            ];
            left.segments.push(nextSegment);
          }
        }
        const right = new ScenarioBorderRing();
        for (const points of mainlandTramlinePolygon.right.coordinates) {
          for (let i = 1; i < points.length; i++) {
            const nextSegment = new ScenarioBorderSegment();
            nextSegment.easyTramSegmentType = 'mainlandTramline';
            nextSegment.coordinates = [
              [points[i - 1][0], points[i - 1][1]],
              [points[i][0], points[i][1]],
            ];
            right.segments.push(nextSegment);
          }
        }

        return [left, right];
      });
    }
    return undefined;
  }

  mainlandCoveragePolygons(): ScenarioBorderRing[] | undefined {
    const coveragePolygons = this._planner.mainland_coverage_polygons;
    if (coveragePolygons !== undefined) {
      return coveragePolygons.map(coveragePolygon => {
        const borderRing = new ScenarioBorderRing();
        for (const points of coveragePolygon.coordinates) {
          for (let i = 1; i < points.length; i++) {
            const nextSegment = new ScenarioBorderSegment();
            nextSegment.easyTramSegmentType = 'mainlandCoverage';
            nextSegment.coordinates = [
              [points[i - 1][0], points[i - 1][1]],
              [points[i][0], points[i][1]],
            ];
            borderRing.segments.push(nextSegment);
          }
        }
        return borderRing;
      });
    }
    return undefined;
  }

  headlandCoveragePolygons(): ScenarioBorderRing[] | undefined {
    const coveragePolygon = this._planner.headland_coverage_polygons;
    if (coveragePolygon !== undefined) {
      return coveragePolygon.map(coveragePolygon => {
        const borderRing = new ScenarioBorderRing();
        console.log('headland coverage polygon: ', coveragePolygon);
        for (const points of coveragePolygon.coordinates) {
            for (let i = 1; i < points.length; i++) {
            const nextSegment = new ScenarioBorderSegment();
            nextSegment.easyTramSegmentType = 'mainlandCoverage';
            nextSegment.coordinates = [
              [points[i - 1][0], points[i - 1][1]],
              [points[i][0], points[i][1]],
            ];
            borderRing.segments.push(nextSegment);
          }
        }
        return borderRing;
      });
    }
    return undefined;
  }

  async toZipFileContent(): Promise<ZipArchive> {
    const archive = new ZipArchive;
    const etramFileBytes = new TextEncoder().encode(this.toEtramFileContent(true));
    await archive.set(this.name + ".ETram", etramFileBytes.buffer);

    // {
    //     const features = this._planner.headland_tramline_polygons?.map((headlandTramlinePolygon, index) => 
    //         tramlinePolygonToFeature(index, headlandTramlinePolygon, Location.Headland)
    //     ) ?? [];
    //     await this.addLayerToZip(archive, "headland_tramline_polygons", features, "POLYGON");
    // }

    // {
    //     const currentTramlinePolygonCount = this._planner.headland_tramline_polygons?.length ?? 0;

    //     const features = this._planner.mainland_tramline_polygons?.map((mainlandTramlinePolygon, index) => 
    //         tramlinePolygonToFeature(currentTramlinePolygonCount + index, mainlandTramlinePolygon, Location.Mainland)
    //     ) ?? [];
    //     await this.addLayerToZip(archive, "mainland_tramline_polygons", features, "POLYGON");
    // }

    // {
    //     const features = this._planner.headland_guidance_tracks?.map((headlandGuidanceTrack) => 
    //         guidanceTrackToFeature(headlandGuidanceTrack, Location.Headland)
    //     ) ?? [];
    //     await this.addLayerToZip(archive, "headland_guidance_tracks", features, "POLYLINE");
    // }

    // {
    //     const features = this._planner.mainland_guidance_tracks?.map((mainlandGuidanceTrack) =>
    //         guidanceTrackToFeature(mainlandGuidanceTrack, Location.Mainland)
    //     ) ?? [];
    //     await this.addLayerToZip(archive, "mainland_guidance_tracks", features, "POLYLINE");
    // }

    const fieldBoundaryCoordinates = [
      this.border.outer,
      ...(this.border.inners ?? []),
    ].map(ring => ring.getCoordinates());

    const fieldBoundaryFeature = {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'MultiPolygon',
        // One field polygon containing its outer ring and all inner exclusion rings.
        coordinates: [fieldBoundaryCoordinates],
      },
    };
    await this.addLayerToZip(
      archive,
      'field_boundary',
      [fieldBoundaryFeature],
      'POLYGON'
    );

    return archive;
  }

  writeShapefile(features: any[], geometryType: shpwrite.OGCGeometry): Promise<{
        shp: DataView;
        shx: DataView;
        dbf: DataView;
      }>
  {
    return new Promise((resolve, reject) => {
        const data = features.map(f => f.properties);
        const geometries = features.map(f => f.geometry.coordinates);

        shpwrite.write(data, geometryType, geometries, (err, result) =>
          err ? reject(err) : resolve(result),
        );    
    })
  }

  async addLayerToZip(zip: ZipArchive, baseName: string, features: any[], geometryType: shpwrite.OGCGeometry) {
    if (features.length === 0) return;

    const files = await this.writeShapefile(features, geometryType);

    await zip.set(`${baseName}.shp`, this.dataViewToArrayBuffer(files.shp));
    await zip.set(`${baseName}.shx`, this.dataViewToArrayBuffer(files.shx));
    await zip.set(`${baseName}.dbf`, this.dataViewToArrayBuffer(files.dbf));
  }

  private dataViewToArrayBuffer(view: DataView): ArrayBuffer {
    return view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength) as ArrayBuffer;
  }

  /**
   * encrypt the file content with AES using the env key
   */ 
  private encryptEtramFileContent(etramFileContent: string): string {
    const keyParsed = Hex.parse(import.meta.env['NG_APP_ETRAM_ENCRYPTION_KEY']);
    const etramFileEncrypted = AES.encrypt(
    etramFileContent,
    keyParsed, {
        mode: ECB,
        padding: Pkcs7
    });
    return etramFileEncrypted.toString();
  }

  /** 
   * @param encrypt whether to encrypt the file content or not
   * @returns file content string
   */
  toEtramFileContent(encrypt: boolean): string {
    const etramFileContent: any = {
      "type": "FeatureCollection",
      "properties": {
        "easytram_version": "1.4",
        "amaplan_version": "0.1", // TODO
        "designator": this.name || "",
        "angle_max_in_degree": this.angleMax,
        "speed_max_in_ms": this.speedMax,
        "overlap_in_percent": this.overlap,
        "wheel_width_in_mm": this.wheelWidthCareDevice * 1000,
        "tramline_width_in_mm": this.trackWidthCareDevice * 1000,
        "working_width_in_mm": this.workingWidthCareDevice * 1000
      },
      "features": []
    };

    this._planner.headland_tramline_polygons?.forEach((headlandTramlinePolygon, index) => {
      etramFileContent.features.push(tramlinePolygonToFeature(index, headlandTramlinePolygon, Location.Headland));
    });

    const currentTramlinePolygonCount = etramFileContent.features.length;
    this._planner.mainland_tramline_polygons?.forEach((mainlandTramlinePolygon, index) => {
      etramFileContent.features.push(tramlinePolygonToFeature(currentTramlinePolygonCount + index, mainlandTramlinePolygon, Location.Mainland));
    });

    this._planner.headland_guidance_tracks?.forEach((headlandGuidanceTrack) => {
      etramFileContent.features.push(guidanceTrackToFeature(headlandGuidanceTrack, Location.Headland));
    });

    this._planner.mainland_guidance_tracks?.forEach((mainlandGuidanceTrack) => {
      etramFileContent.features.push(guidanceTrackToFeature(mainlandGuidanceTrack, Location.Mainland));
    });

    const stringifiedContent = JSON.stringify(etramFileContent, null, 2);

    if (encrypt) {
        return this.encryptEtramFileContent(stringifiedContent);
    }

    return stringifiedContent;
  }

  validateMachineConfiguration(): boolean {
    if (this._planner.plant_care_swath === undefined || this._planner.plant_care_swath <= 0) return false;
    if (this._planner.wheel_track === undefined || this._planner.wheel_track <= 0) return false;
    if (this._planner.tire_width === undefined || this._planner.tire_width <= 0) return false;
    return true;
  }

  validateHeadlandConfiguration(): boolean {
    if (this._planner.headland_mode !== undefined && this._planner.headland_mode !== 'off') {
      if (this._planner.headland_width === undefined || this._planner.headland_width <= 0) return false;
    }
    return true;
  }

  validateCorelandConfiguration(): boolean {
    if (this.corelandBaseSegment === undefined) return false;
    if (this.degreeOffset === undefined) return false;
    if (this.shiftOffset === undefined) return false;

    try {
      const clone = this.clone();
      if (clone.mainlandGuidanceTracks() === undefined || clone.mainlandTramlinePolygons() === undefined) {
        // Planner was not able to calculate guidance tracks and tramline polygons with the current configuration, so we consider it as invalid for the next step
        return false;
      }
    } catch (e) {
      // In case the planner throws an error (e.g. because the coreland reference line is invalid), we also consider the configuration as invalid for the next step
      return false;
    }

    return true;
  }

  currentStateValidForNextStep(): boolean {
    switch (this.state) {
      case EASYTRAMPLANSTATE.MachineSelection:
        return this.validateMachineConfiguration();
      case EASYTRAMPLANSTATE.HeadlandCreation:
        return this.validateHeadlandConfiguration();
      case EASYTRAMPLANSTATE.CorelandCreation:
        return this.validateCorelandConfiguration();
      case EASYTRAMPLANSTATE.Finished:
        return true;
      default:
        return false;
    }
  }

  nextPlanningStep(): EasyTramPlan {
    const updatedEasyTramPlan = this.clone();
    switch (updatedEasyTramPlan.state) {
      case EASYTRAMPLANSTATE.MachineSelection:
        updatedEasyTramPlan.state = EASYTRAMPLANSTATE.HeadlandCreation;
        break;
      case EASYTRAMPLANSTATE.HeadlandCreation:
        updatedEasyTramPlan.state = EASYTRAMPLANSTATE.CorelandCreation;
        break;
      case EASYTRAMPLANSTATE.CorelandCreation:
        updatedEasyTramPlan.state = EASYTRAMPLANSTATE.Finished;
        break;
      case EASYTRAMPLANSTATE.Finished:
        updatedEasyTramPlan.state = EASYTRAMPLANSTATE.Finished;
        break;
    }

    return updatedEasyTramPlan;
  }

  previousPlanningStep(): EasyTramPlan {
    const updatedEasyTramPlan = this.clone();
    console.log('PREV STEP begin this: ', this);
    console.log('PREV: current state: ', updatedEasyTramPlan.state);
    switch (updatedEasyTramPlan.state) {
      case EASYTRAMPLANSTATE.MachineSelection:
        updatedEasyTramPlan.state = EASYTRAMPLANSTATE.MachineSelection;
        break;
      case EASYTRAMPLANSTATE.HeadlandCreation:
        updatedEasyTramPlan.state = EASYTRAMPLANSTATE.MachineSelection;
        break;
      case EASYTRAMPLANSTATE.CorelandCreation:
        updatedEasyTramPlan.state = EASYTRAMPLANSTATE.HeadlandCreation;
        break;
      case EASYTRAMPLANSTATE.Finished:
        updatedEasyTramPlan.state = EASYTRAMPLANSTATE.CorelandCreation;
        break;
    }

    return updatedEasyTramPlan;
  }

  getCorrespondingSelectionModeHandler() {
    switch (this.state) {
      case EASYTRAMPLANSTATE.CorelandCreation:
        return new SelectionModeSegmentHandler();
      case EASYTRAMPLANSTATE.MachineSelection:
      case EASYTRAMPLANSTATE.HeadlandCreation:
      case EASYTRAMPLANSTATE.Finished:
      default:
        return SelectionModeNoneHandler;
    }
  }
}

export enum EASYTRAMPLANSTATE {
  MachineSelection,
  HeadlandCreation,
  CorelandCreation,
  Finished,
}

function guidanceTrackToFeature(guidanceTrack: Wgs84LineString, location: Location): any {
  const feature = {
    "type": "Feature",
    "properties": {
      "type": "GuidanceLine",
      "line_id": guidanceTrack.line_id,
      "line_location": location,
    },
    "geometry": {
      "type": "MultiLineString",
      "coordinates": [
        guidanceTrack.coordinates.map(coord => [coord[0], coord[1]])
      ]
    }
  };

  return feature;
}

function tramlinePolygonToFeature(id: number, tramlinePolygon: TramlinePolygon<"WGS84">, location: Location): any {
  const feature = {
    "type": "Feature",
    "properties": {
      "type": "TramLine",
      "id": id,
      "ref_line_id": tramlinePolygon.left.ref_line_id,
      "line_location": location,
    },
    "geometry": {
      "type": "MultiPolygon",
      "coordinates": [
        tramlinePolygon.left.coordinates.map(leftTramline => {
          return leftTramline.map(coord => [coord[0], coord[1]]);
        }),
        tramlinePolygon.right.coordinates.map(rightTramline => {
          return rightTramline.map(coord => [coord[0], coord[1]]);
        })
      ]
    }
  };

  return feature;
}

enum Location {
  Mainland = 0,
  Headland = 1,
}
